import { createContext, useContext, useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import getConfig from '../../config/environments';
import { PriceFeedClient, priceKey } from './price-feed-client';

const PriceFeedContext = createContext(null);

export function PriceFeedProvider({ children }) {
  const [client] = useState(() => {
    const { socket } = getConfig();
    return new PriceFeedClient({ url: socket?.url || '', apiKey: socket?.apiKey || '' });
  });
  useEffect(() => {
    client.connect();
    return () => client.disconnect();
  }, [client]);
  const value = useMemo(() => ({ client }), [client]);
  return <PriceFeedContext.Provider value={value}>{children}</PriceFeedContext.Provider>;
}

export function usePriceFeedClient() {
  const context = useContext(PriceFeedContext);
  if (!context) throw new Error('usePriceFeedClient must be used within PriceFeedProvider');
  return context.client;
}

export function useLivePrice(symbol, market) {
  const client = usePriceFeedClient();
  const key = symbol && market ? priceKey(symbol, market) : null;
  const [update, setUpdate] = useState(() => (key ? client.getLastPrice(key) : undefined));
  useEffect(() => {
    setUpdate(key ? client.getLastPrice(key) : undefined);
    return key ? client.subscribe(key, setUpdate) : undefined;
  }, [client, key]);
  return update;
}

export function useLivePrices(keys) {
  const client = usePriceFeedClient();
  const [prices, setPrices] = useState(new Map());
  const stableKeys = useMemo(() => [...keys].sort().join(','), [keys]);
  useEffect(() => {
    const unsubscribers = (stableKeys ? stableKeys.split(',') : []).map((key) =>
      client.subscribe(key, (update) => {
        setPrices((previous) => new Map(previous).set(priceKey(update.symbol, update.market), update));
      }),
    );
    return () => unsubscribers.forEach((unsubscribe) => unsubscribe());
  }, [client, stableKeys]);
  return prices;
}

export function usePriceFeedStatus() {
  const client = usePriceFeedClient();
  return useSyncExternalStore(
    (listener) => client.onStatusChange(listener),
    () => client.isConnected(),
    () => false,
  );
}
